import { useState, useMemo, useCallback } from 'react';
import { MaterialData } from '../../../../../../types/materials';

interface WireFilters {
  status: string[];
  lotNumber: string[];
  serialNumber: string[];
  manufacturer: string[];
  batchNumber: string[];
}

interface UseWireFiltersProps {
  materials: MaterialData[];
}

export const useWireFilters = ({ materials }: UseWireFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<WireFilters>({
    status: [],
    lotNumber: [],
    serialNumber: [],
    manufacturer: [],
    batchNumber: [],
  });

  // Extract unique lot numbers from materials
  const uniqueLotNumbers = useMemo(() => {
    const lotNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.wireLotNumber) {
        lotNumbers.add(material.details.wireLotNumber);
      }
    });
    return Array.from(lotNumbers).sort();
  }, [materials]);

  // Extract unique serial numbers from materials
  const uniqueSerialNumbers = useMemo(() => {
    const serialNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.wireSerialNumber) {
        serialNumbers.add(material.details.wireSerialNumber);
      }
    });
    return Array.from(serialNumbers).sort();
  }, [materials]);

  // Extract unique manufacturers from materials
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.wireManufacturer) {
        manufacturers.add(material.details.wireManufacturer);
      }
    });
    return Array.from(manufacturers).sort();
  }, [materials]);

  // Extract unique batch numbers from materials
  const uniqueBatchNumbers = useMemo(() => {
    const batchNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.wireBatchNumber) {
        batchNumbers.add(material.details.wireBatchNumber);
      }
    });
    return Array.from(batchNumbers).sort();
  }, [materials]);

  // Filter materials based on search query and filters
  const filteredMaterials = useMemo(() => {
    return materials.filter((material: MaterialData) => {
      // Search query filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        material.materialId?.toString().includes(searchLower) ||
        material.details?.wireLotNumber?.toLowerCase().includes(searchLower) ||
        material.details?.wireSerialNumber?.toLowerCase().includes(searchLower) ||
        material.details?.wireManufacturer?.toLowerCase().includes(searchLower) ||
        material.details?.wireBatchNumber?.toLowerCase().includes(searchLower) ||
        material.assemblyNumbers?.some((assembly: string) => assembly?.toLowerCase().includes(searchLower)) ||
        (material.materialStatus)?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = filters.status.length === 0 || 
        filters.status.includes(material.materialStatus || '');

      // Lot number filter
      const matchesLotNumber = filters.lotNumber.length === 0 || 
        (material.details?.wireLotNumber && filters.lotNumber.includes(material.details.wireLotNumber));

      // Serial number filter
      const matchesSerialNumber = filters.serialNumber.length === 0 || 
        (material.details?.wireSerialNumber && filters.serialNumber.includes(material.details.wireSerialNumber));

      // Manufacturer filter
      const matchesManufacturer = filters.manufacturer.length === 0 || 
        (material.details?.wireManufacturer && filters.manufacturer.includes(material.details.wireManufacturer));

      // Batch number filter
      const matchesBatchNumber = filters.batchNumber.length === 0 || 
        (material.details?.wireBatchNumber && filters.batchNumber.includes(material.details.wireBatchNumber));

      return matchesSearch && matchesStatus && matchesLotNumber && 
             matchesSerialNumber && matchesManufacturer && matchesBatchNumber;
    });
  }, [materials, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.status.length > 0 || 
           filters.lotNumber.length > 0 ||
           filters.serialNumber.length > 0 ||
           filters.manufacturer.length > 0 ||
           filters.batchNumber.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof WireFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      status: [],
      lotNumber: [],
      serialNumber: [],
      manufacturer: [],
      batchNumber: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueLotNumbers,
    uniqueSerialNumbers,
    uniqueManufacturers,
    uniqueBatchNumbers,
    filteredMaterials,
    hasActiveFilters,
    filteredCount: filteredMaterials.length,
    onResetFilters,
  };
}; 